import React, { useState } from "react";
import {
  View,
  TextInput,
  TouchableOpacity,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
  Linking,
  SafeAreaView,
} from "react-native";
import { StyleSheet, scale } from "@/utils/appStyleSheet";
import { router, useLocalSearchParams } from "expo-router";
import validator from "validator";
import { FontFamilies } from "@/constants/Typography";
import PaymentMethodServices from "@/services/paymentMethodServices";
import { AppText } from "@/components/AppText";
import { showError, showSuccess } from "@/utils/toast";
import Header from "@/components/ui/Header";
import Icons from "@/assets/icons";
import useOfferStore from "@/store/offer";

const RegisterPaymentMethod: React.FC = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { triggerSuccessModal } = useOfferStore();
  const { redirectUrl, amount } = useLocalSearchParams<{
    redirectUrl?: string;
    amount?: string;
  }>();

  const handleClaimOffer = async () => {
    if (!email) return;

    if (!validator.isEmail(email)) {
      showError("Please enter a valid email address.", "Invalid Email");
      return;
    }

    setIsLoading(true);
    try {
      const paymentMethod = await PaymentMethodServices.registerPaymentMethod({
        email,
        provider: "PAYPAL",
      });

      if (paymentMethod) {
        // Show success message for payment method registration
        showSuccess("Payment method added successfully");

        if (redirectUrl && redirectUrl.trim() !== '') {
          // Perform preflight check to verify offer eligibility
          // const preflightResult = await AdgemServices.preflightCheck(
          //   redirectUrl
          // );

          // if (!preflightResult.success) {
          //   showError(preflightResult.message, "Offer Unavailable");
          //   router.back();
          //   return;
          // }

          // Check if URL can be opened and redirect to offer
          const canOpen = await Linking.canOpenURL(redirectUrl);
          if (canOpen) {
            await Linking.openURL(redirectUrl);
            triggerSuccessModal();
            router.back();
          } else {
            showError("Unable to open this offer. Please try again later.");
            router.back();
          }
        } else {
          router.back();
        }
      }
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong. Please try again.";
      showError(errorMessage);
      console.error("Error registering payment method:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <Header title="Add Payment Method" />
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          {/* Main Content */}
          <View style={styles.content}>
            {/* Title Section */}
            <View style={styles.titleSection}>
              <AppText style={styles.title}>
                What&apos;s your PayPal account email?
              </AppText>
              <AppText style={styles.subtitle}>
                We&apos;ll send your {amount ? `$${amount}` : "reward"} signup
                offer to this account.
              </AppText>
            </View>

            {/* Input Section */}
            <View style={styles.inputSection}>
              <AppText style={styles.inputLabel}>PayPal account email</AppText>
              <View style={styles.inputContainer}>
                <TextInput
                  style={styles.input}
                  value={email}
                  onChangeText={setEmail}
                  placeholder="PayPal account email"
                  placeholderTextColor="rgba(22, 29, 20, 0.3)"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  autoCorrect={false}
                  autoComplete="email"
                />
              </View>
            </View>
          </View>

          {/* Bottom Section with Button and Security Icons */}
          <View style={styles.bottomSection}>
            <TouchableOpacity
              style={[
                styles.claimButton,
                (!email || isLoading) && styles.claimButtonDisabled,
              ]}
              onPress={handleClaimOffer}
              disabled={!email || isLoading}
            >
              {isLoading ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <View style={styles.buttonContent}>
                  <AppText style={styles.claimButtonText}>
                    Claim signup offer
                  </AppText>
                  <Icons.ArrowCircleInner
                    width={scale(14)}
                    height={scale(14)}
                    fill="#FFFFFF"
                  />
                </View>
              )}
            </TouchableOpacity>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F8FAFC",
  },
  keyboardAvoid: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 16,
  },
  content: {
    flex: 1,
    paddingTop: 20,
  },
  titleSection: {
    marginBottom: 32,
  },
  title: {
    fontSize: 30,
    fontFamily: FontFamilies.InterTight.SemiBold,
    color: "#000000",
    lineHeight: 36,
    marginBottom: 20,
  },
  subtitle: {
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "rgba(22, 29, 20, 0.7)",
    lineHeight: 23,
    letterSpacing: -0.3,
  },
  inputSection: {
    marginBottom: 24,
  },
  inputLabel: {
    fontSize: 13,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    marginBottom: 6,
    letterSpacing: -0.26,
  },
  inputContainer: {
    borderWidth: 1,
    borderColor: "#EBEBEB",
    borderRadius: 4,
    backgroundColor: "#FFFFFF",
  },
  input: {
    paddingVertical: 13,
    paddingHorizontal: 12,
    fontSize: 15,
    fontFamily: FontFamilies.Inter.Regular,
    color: "#161D14",
    letterSpacing: -0.3,
  },
  bottomSection: {
    paddingBottom: 16,
  },
  claimButton: {
    backgroundColor: "#0F9129",
    borderRadius: 100,
    paddingVertical: 16.5,
    paddingHorizontal: 20,
    alignItems: "center",
    marginBottom: 16,
  },
  claimButtonDisabled: {
    opacity: 0.5,
  },
  claimButtonText: {
    fontSize: 17,
    fontFamily: FontFamilies.Inter.Bold,
    color: "#FFFFFF",
    lineHeight: 23,
  },
  buttonContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
});

export default RegisterPaymentMethod;
